/**
 * Monthly Intelligence Report (PE-2141 FE, backend PE-2140).
 *
 * Shapes mirror osprey-api's `src/domain/intelligence-reports` on
 * origin/development — the prisma models + the controller's declared responses,
 * read from source rather than the BFF swagger.
 */

/**
 * Report lifecycle. Read off the backend's OWN state model
 * (`enum IntelligenceReportStatus`), which has THREE states, not the two the
 * ticket prose implies. Approve and release are separate transitions:
 *
 *   PENDING_REVIEW --approve--> APPROVED --release--> RELEASED
 *
 * The service rejects any other move: `approve` 400s unless the report is
 * PENDING_REVIEW, and `release` 400s unless it is APPROVED. There is no
 * "reject" transition and no backwards move — regenerating is
 * `POST /generate` with `force: true`, which deletes and recreates the record.
 *
 * ⚠️ APPROVED is NOT visible to the merchant. Only RELEASED is.
 */
export type IntelligenceReportStatus = 'PENDING_REVIEW' | 'APPROVED' | 'RELEASED';

/**
 * AI_DRAFT is the untouched generated version, retained for audit even after a
 * TCS admin edits the copy. RELEASED is the version actually published — it is
 * created either at approve time (when the admin supplied edited `content`) or
 * at release time (promoting the draft verbatim).
 */
export type IntelligenceReportVersionType = 'AI_DRAFT' | 'RELEASED';

/**
 * How the narrative was produced. The backend picks this per generation from
 * `REPORT_AI_ENABLED`: 'AI' = Vertex AI Gemini, 'RULE_BASED' = the deterministic
 * threshold-driven writer. Surfaced in the UI because a rule-based report is a
 * materially different artefact from an AI-written one and a reviewer should
 * not have to guess which they are reading.
 */
export type IntelligenceReportGenerationMethod = 'AI' | 'RULE_BASED';

/** Version rows as returned by the LIST route — metadata only, no content. */
export interface IntelligenceReportVersionSummary {
  id: string;
  versionType: IntelligenceReportVersionType;
  generationMethod: IntelligenceReportGenerationMethod;
  createdAt: string;
}

/**
 * Version rows as returned by `GET /intelligence-reports/:id` — the full record
 * including the markdown `content` and the `rawData` provenance blob.
 *
 * `rawData` is only ever written on the AI_DRAFT version (see the service's
 * `generate`); the RELEASED version is created later from content alone and
 * carries none. Provenance must therefore be read off the DRAFT, not the
 * released copy — see `provenance.ts`.
 */
export interface IntelligenceReportVersion extends IntelligenceReportVersionSummary {
  content: string;
  rawData?: AggregatedAnalyticsData | null;
}

/** List-route row. `versions` carries metadata only. */
export interface IntelligenceReportListItem {
  id: string;
  merchantId: string;
  /** Report period, always `YYYY-MM`. */
  period: string;
  status: IntelligenceReportStatus;
  generatedAt: string;
  approvedAt?: string | null;
  approvedBy?: string | null;
  releasedAt?: string | null;
  releasedBy?: string | null;
  versions: IntelligenceReportVersionSummary[];
}

/** Detail-route record — same row, but every version carries content + rawData. */
export interface IntelligenceReport extends Omit<IntelligenceReportListItem, 'versions'> {
  versions: IntelligenceReportVersion[];
}

/* -------------------------------------------------------------------------- */
/* Analytics provenance                                                        */
/* -------------------------------------------------------------------------- */

/**
 * The four upstream analytics sources the report is aggregated from.
 * Each is INDEPENDENTLY nullable in `rawData`: the backend's `aggregate()`
 * resolves a source to `null` when either no property id is configured for the
 * merchant OR the upstream call threw (it catches and logs, it does not fail the
 * report). Distinguishing those two cases needs the config — see `provenance.ts`.
 */
export type AnalyticsSourceKey = 'ga4' | 'sentry' | 'clarity' | 'meta';

export interface Ga4Data {
  sessions: number;
  users: number;
  newUsers: number;
  bounceRate: number;
  avgSessionDuration: number;
  topPages: Array<{ page: string; views: number }>;
  conversionRate: number | null;
  funnelSteps: Array<{ step: string; users: number; dropOffRate: number }>;
}

export interface SentryData {
  totalErrors: number;
  uniqueErrors: number;
  topErrors: Array<{ title: string; count: number; firstSeen: string }>;
  crashFreeSessionRate: number | null;
  performanceIssues: number;
}

export interface ClarityData {
  totalSessions: number;
  engagedSessions: number;
  deadClicks: number;
  /** Backend spells this `rageclicks` (lower-case c) — do not "fix" it here. */
  rageclicks: number;
  excessiveScrolling: number;
  topScrolledPages: Array<{ page: string; scrollDepth: number }>;
}

export interface MetaData {
  impressions: number;
  reach: number;
  clicks: number;
  /**
   * ⚠️ MONEY, and deliberately NOT rendered as currency anywhere in this feature.
   * It comes straight from the Meta Graph API in the ad account's own currency,
   * and neither the aggregate nor the report record carries a currency code or a
   * minor/major-unit marker. Formatting it as ₦ (or scaling it by 100 the way a
   * kobo field would need) would be inventing a fact the backend never supplied.
   * The narrative prose may quote it; the provenance panel shows counts and rates
   * only. See the `sales-reporting-minor-units-trap` precedent.
   */
  spend: number;
  conversions: number | null;
  ctr: number | null;
}

/** `rawData` on the AI_DRAFT version — the aggregator's output, as stored. */
export interface AggregatedAnalyticsData {
  period: string;
  merchantId: string;
  ga4: Ga4Data | null;
  sentry: SentryData | null;
  clarity: ClarityData | null;
  meta: MetaData | null;
}

/* -------------------------------------------------------------------------- */
/* Per-merchant analytics config                                               */
/* -------------------------------------------------------------------------- */

/**
 * `GET/PUT /intelligence-reports/config/:merchantId`.
 *
 * Two jobs in one record: the four property ids that decide WHICH sources can be
 * aggregated at all, and seven thresholds that tune when the rule-based writer
 * flags a metric. Every field is nullable; a null threshold falls back to the
 * backend default (mirrored in THRESHOLD_FIELDS below).
 *
 * NB: the monthly scheduler iterates `MerchantAnalyticsConfig` rows — a merchant
 * with NO config row is never auto-reported on. Creating the config is therefore
 * the act that opts a merchant into the monthly cadence.
 */
export interface MerchantAnalyticsConfig {
  merchantId: string;
  ga4PropertyId: string | null;
  clarityProjectId: string | null;
  sentryProjectSlug: string | null;
  metaPixelId: string | null;
  bounceRateThreshold: number | null;
  conversionRateThreshold: number | null;
  dropOffRateThreshold: number | null;
  errorCountThreshold: number | null;
  rageClickThreshold: number | null;
  deadClickThreshold: number | null;
  ctrThreshold: number | null;
}

export type UpsertAnalyticsConfigPayload = Partial<Omit<MerchantAnalyticsConfig, 'merchantId'>>;

export interface GenerateReportPayload {
  merchantId: string;
  period: string;
  force?: boolean;
}

export interface ListReportsQuery {
  merchantId?: string;
  period?: string;
  status?: IntelligenceReportStatus;
}

/* -------------------------------------------------------------------------- */
/* Display metadata                                                            */
/* -------------------------------------------------------------------------- */

export const STATUS_LABELS: Record<IntelligenceReportStatus, string> = {
  PENDING_REVIEW: 'Pending review',
  APPROVED: 'Approved',
  RELEASED: 'Released',
};

/**
 * Tone is meaningful here, not decorative: amber = the merchant cannot see this,
 * blue = approved but STILL not sent, emerald = the merchant can see it. The
 * middle state is the one people get wrong, so it stays visually distinct from
 * released rather than sharing a "done" green.
 */
export const STATUS_STYLES: Record<IntelligenceReportStatus, string> = {
  PENDING_REVIEW: 'bg-amber-100 text-amber-800',
  APPROVED: 'bg-blue-100 text-blue-700',
  RELEASED: 'bg-emerald-100 text-emerald-700',
};

/** One-line explanation of what each state means for merchant visibility. */
export const STATUS_HINTS: Record<IntelligenceReportStatus, string> = {
  PENDING_REVIEW: 'AI draft awaiting TCS review — not visible to the merchant.',
  APPROVED: 'Reviewed and approved, but not yet released — still not visible to the merchant.',
  RELEASED: 'Published — the merchant can see this report.',
};

export const GENERATION_METHOD_LABELS: Record<IntelligenceReportGenerationMethod, string> = {
  AI: 'AI-generated',
  RULE_BASED: 'Rule-based',
};

export const SOURCE_LABELS: Record<AnalyticsSourceKey, string> = {
  ga4: 'Google Analytics 4',
  sentry: 'Sentry',
  clarity: 'Microsoft Clarity',
  meta: 'Meta Pixel',
};

/** Which config id field switches each source on, for the "not connected" copy. */
export const SOURCE_CONFIG_FIELD: Record<AnalyticsSourceKey, keyof MerchantAnalyticsConfig> = {
  ga4: 'ga4PropertyId',
  sentry: 'sentryProjectSlug',
  clarity: 'clarityProjectId',
  meta: 'metaPixelId',
};

export const SOURCE_CONFIG_LABELS: Record<AnalyticsSourceKey, string> = {
  ga4: 'GA4 property ID',
  sentry: 'Sentry project slug',
  clarity: 'Clarity project ID',
  meta: 'Meta Pixel ID',
};

/**
 * Threshold inputs, with the backend's own fallback values. `defaultValue` is
 * mirrored from `rule-based-report.service.ts` DEFAULT_THRESHOLDS so the form can
 * show what a blank field will actually do instead of just "unset".
 *
 * `kind` drives input step/suffix: rates are 0–1 fractions server-side, so the
 * form shows them as such rather than pretending they are percentages.
 */
export const THRESHOLD_FIELDS: Array<{
  field: keyof UpsertAnalyticsConfigPayload;
  label: string;
  hint: string;
  kind: 'rate' | 'count';
  defaultValue: number;
}> = [
  { field: 'bounceRateThreshold', label: 'Bounce rate', hint: 'Flagged when above this', kind: 'rate', defaultValue: 0.55 },
  { field: 'conversionRateThreshold', label: 'Conversion rate', hint: 'Flagged when below this', kind: 'rate', defaultValue: 0.02 },
  { field: 'dropOffRateThreshold', label: 'Funnel drop-off rate', hint: 'Flagged when above this', kind: 'rate', defaultValue: 0.5 },
  { field: 'ctrThreshold', label: 'Ad click-through rate', hint: 'Flagged when below this', kind: 'rate', defaultValue: 0.01 },
  { field: 'errorCountThreshold', label: 'Error count', hint: 'Flagged when above this', kind: 'count', defaultValue: 50 },
  { field: 'rageClickThreshold', label: 'Rage clicks', hint: 'Flagged when above this', kind: 'count', defaultValue: 10 },
  { field: 'deadClickThreshold', label: 'Dead clicks', hint: 'Flagged when above this', kind: 'count', defaultValue: 10 },
];
